/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/no-misused-promises */

'use client'

import React, { useState, useEffect, useRef } from 'react'
import Image from 'next/image'
import assetLoader from '@utils/internalImageLoader'
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button'
import StyledComponentsRegistry from '@configs/registry'
import { validateVehicleNumber } from '@components/MainWidget/FastagWidget/helper'
import { vehicleInfoService } from '@services/vehicleInfoService'
import { ThemeProvider, AckoMainTheme } from '@acko-tech/building-blocks.ui.theme'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { useEnvVariablesClientConfig } from '@context/EnvClientContext/EnvVariablesClientContext'
import {
  INPUT_ICON,
  INPUT_ICON_LOGO,
  TAP_BTN_EVENT,
  PHONE_NUMBER_ENTERED,
} from '@components/MainWidget/FastagWidget/constants'
import CreateLead from '@services/createLead'
import { segmentEventCommonPropFunc } from '@utils/segmentProperties'
import {
  VAS_WIDGET_INPUT_PLACEHOLDER,
  WIDGET_INPUT,
  REG_NO_ERROR,
  HSRP_DATA,
  HSRP_WIDGET_INPUT,
  BOOK_HSRP,
} from '@components/MainWidget/ChallanWidgetLanding/constants'
import ReturningUserInfoBlock from '@components/MainWidget/ChallanWidgetLanding/returningUserInfoBlock'
import MobileNumberModal from '@components/MainWidget/ChallanWidgetModals/mobileNumberInputModal'
import VasAppRedirectWrapper from '@components/MainWidget/ChallanWidgetModals/VasAppRedirectWrapper'
import { newHsrpLoadingScreen } from '@components/MainWidget/ChallanWidgetModals/constants'
import LoadingScreen from '@components/MainWidget/ChallanWidgetModals/LoadingScreen'
import { getVasFinalScreenData } from '@factories/vasWidgetsFactory'
import {
  fetchDataFromLS,
  getHsrpMMVStoreData,
  getHsrpProductName,
  getHsrpSourceName,
  getHsrpApiKeyLead,
} from '@components/MainWidget/ChallanWidgetLanding/helper'
import { widgetJourney } from '@configs/segmentEvents.config'
import { HsrpUserData, HsrpWidgetLandingProps, initUserData } from '@interfaces/hsrp'
import { HSRP_LOADER_TIMEOUT } from '@constants/times'
import { useUserContext } from '@context/UserContext/UserContenxt'
import styles from './hsrpWidgetLanding.module.scss'

const HsrpWidgetLanding: React.FC<HsrpWidgetLandingProps> = data => {
  const {
    widget,
    widgetHeading,
    regexPatternsData,
    widgetCtaText,
    inputPlaceholder,
    isMobileDevice,
    hsrpFinalScreenData,
    widgetOptions,
    segmentEventCommonProperties,
    isBottomFloater,
    onHide,
  } = data

  const hsrpWrapperProps = getVasFinalScreenData(hsrpFinalScreenData)
  const [regNo, setRegNo] = useState('')
  const [error, setError] = useState('')
  const { isLoggedIn, userData } = useUserContext()
  const [returningUserResponse, setReturningUserResponse] = useState<any>(null)
  const [hsrpUserData, setHsrpUserData] = useState<HsrpUserData>(initUserData)
  const [showHsrpLoading, setShowHsrpLoading] = useState<boolean>(false)
  const [showMobileNumberModal, setShowMobileNumberModal] = useState<boolean>(false)
  const [showMobileChallanModal, setShowMobileChallanModal] = useState<boolean>(false)
  const [showMwebChallanModal, setShowMwebChallanModal] = useState<boolean>(true)
  const [showDoMoreThanChallanModal, setShowDoMoreThanChallanModal] = useState<boolean>(false)
  const [properties, setProperties] = useState<Record<string, string> | null>(null)
  const inputPlaceHolder = inputPlaceholder || VAS_WIDGET_INPUT_PLACEHOLDER
  const inputRef = useRef<HTMLInputElement | null>(null)
  const config = useEnvVariablesClientConfig()
  const currentEnv = config?.ACKO_ENV
  const hsrpFallbackScreenData = widgetOptions?.hsrpFallbackScreenData

  useEffect(() => {
    ;(async () => {
      const resolvedProperties = await segmentEventCommonPropFunc(segmentEventCommonProperties)
      setProperties(resolvedProperties)
    })()
  }, [segmentEventCommonProperties])

  useEffect(() => {
    ;(async () => {
      const localStorageData = await fetchDataFromLS(HSRP_DATA)
      setReturningUserResponse(localStorageData)
      setHsrpUserData({ ...hsrpUserData, mobile_number: localStorageData?.mobile_number })
    })()
  }, [showHsrpLoading])

  const onClickSegmentEvent = (eventName, mobileNumber, regNum) => {
    trackEvent(eventName, {
      ...properties,
      ...{ journey: widgetJourney[widget] },
      product: getHsrpProductName(),
      phone: mobileNumber || '',
      registration_number: regNum || '',
    })
  }

  const fetchVehicleAndProceed = async (regNumber, mobileNumber) => {
    if (!regNumber) return
    try {
      setShowHsrpLoading(true)
      const vehicleInfo = await vehicleInfoService(regNumber)
      if (!vehicleInfo?.registration_number) {
        setShowHsrpLoading(false)
        return
      }
      setHsrpUserData(prev => ({
        ...prev,
        reg_number: regNumber,
        mobile_number: mobileNumber,
        vehicleInfo,
      }))
      CreateLead({
        registrationNumber: regNumber,
        phoneNo: mobileNumber,
        productKey: getHsrpProductName(),
        sourceName: getHsrpSourceName(),
        apiKey: getHsrpApiKeyLead(currentEnv),
      })
    } catch (error) {
      console.error('Error fetching vehicle info:', error, regNumber)
    }
  }

  const handleRegNoChange = e => {
    const newRegNumber = e?.target?.value.toUpperCase()
    setRegNo(newRegNumber)
    setHsrpUserData({ ...hsrpUserData, reg_number: newRegNumber })
  }

  const onFormSubmit = e => {
    setError('')
    e.preventDefault()
    inputRef?.current?.blur()
    const vehicleError = validateVehicleNumber(regNo, regexPatternsData)
    if (!regNo || vehicleError) {
      setError(REG_NO_ERROR)
      return
    }
    if (isLoggedIn && userData?.phone) {
      fetchVehicleAndProceed(regNo, userData?.phone)
      return
    }
    if (!hsrpUserData?.mobile_number) {
      setShowMobileNumberModal(true)
    } else {
      fetchVehicleAndProceed(regNo, hsrpUserData?.mobile_number)
    }
    onClickSegmentEvent(TAP_BTN_EVENT, hsrpUserData?.mobile_number || userData?.phone, regNo)
  }

  const handleMobileNumberSubmit = async (mobileNumber: string) => {
    setShowMobileNumberModal(false)
    fetchVehicleAndProceed(regNo, mobileNumber)
    onClickSegmentEvent(PHONE_NUMBER_ENTERED, mobileNumber, regNo)
  }

  const handleReturningSearch = () => {
    setHsrpUserData(returningUserResponse)
    setShowHsrpLoading(true)
    onClickSegmentEvent(TAP_BTN_EVENT, returningUserResponse?.mobile_number, returningUserResponse?.reg_number)
  }

  const handleLoadingComplete = async () => {
    await getHsrpMMVStoreData(hsrpUserData)
    setShowHsrpLoading(false)
    setShowMobileChallanModal(true)
    setShowMwebChallanModal(true)
  }

  const handleFallbackModal = () => {
    setShowHsrpLoading(false)
    setShowDoMoreThanChallanModal(false)
    setShowMobileChallanModal(false)
    setRegNo('')
    setError('')
  }

  return (
    <ThemeProvider theme={AckoMainTheme}>
      <>
        <div>
          {showHsrpLoading && (
            <LoadingScreen
              isMobile={isMobileDevice}
              screens={newHsrpLoadingScreen}
              duration={HSRP_LOADER_TIMEOUT}
              regNo={regNo || hsrpUserData?.reg_number}
              onComplete={() => handleLoadingComplete()}
              isBottomFloater={isBottomFloater}
              showLoadingScreen={showHsrpLoading}
            />
          )}

          {showMobileChallanModal && (
            <VasAppRedirectWrapper
              mobileNo={hsrpUserData?.mobile_number}
              isMobile={isMobileDevice}
              vasWrapperProps={hsrpWrapperProps}
              vasFallbackScreenData={hsrpFallbackScreenData}
              regNum={hsrpUserData?.reg_number}
              vehicleInfoData={hsrpUserData}
              segmentEventCommonProperties={properties}
              setShowMobileChallanModal={handleFallbackModal}
              isBottomFloater={isBottomFloater}
              onHide={onHide}
              showMwebChallanModal={showMwebChallanModal}
              setShowMwebChallanModal={setShowMwebChallanModal}
              showDoMoreThanChallanModal={showDoMoreThanChallanModal}
              setShowDoMoreThanChallanModal={setShowDoMoreThanChallanModal}
              widgetType={widget}
            />
          )}

          {showMobileNumberModal && (
            <MobileNumberModal
              isMobile={isMobileDevice}
              showMobileNumberModal={showMobileNumberModal}
              setShowMobileNumberModal={setShowMobileNumberModal}
              onSubmit={(mobileNumber: string) => {
                handleMobileNumberSubmit(mobileNumber).catch(console.error)
              }}
              widgetType={widget}
            />
          )}
        </div>
        <div id="hsrp-widget-landing-widget" className={styles.widgetRootContainer}>
          {returningUserResponse?.vehicleInfo?.registration_number && (
            <ReturningUserInfoBlock
              userInfo={returningUserResponse}
              handleReturningSearch={handleReturningSearch}
              widgetType={widget}
            />
          )}
          <form className={`${styles.widgetForm}`} onSubmit={e => onFormSubmit(e)}>
            <label htmlFor={WIDGET_INPUT} className={styles.widgetHeading}>
              {widgetHeading}
            </label>
            <div className={error ? styles.inputErrorStyles : styles.inputBoxContainer}>
              <Image
                width={0}
                height={0}
                src={`${assetLoader()}${INPUT_ICON}`}
                className={styles.inputIcon}
                alt={INPUT_ICON_LOGO}
              />
              <input
                type="text"
                id={HSRP_WIDGET_INPUT}
                ref={inputRef}
                placeholder={inputPlaceHolder}
                onChange={handleRegNoChange}
                autoComplete="off"
                value={regNo}
                maxLength={10}
              />
            </div>
            {error && <div className={styles.inputError}>{error}</div>}
            <StyledComponentsRegistry>
              <PrimaryButton className={styles.primaryButton}>{widgetCtaText || BOOK_HSRP}</PrimaryButton>
            </StyledComponentsRegistry>
          </form>
        </div>
      </>
    </ThemeProvider>
  )
}

export default HsrpWidgetLanding
